import { Box, Checkbox, Grid, IconButton, MenuItem, Popover, Stack, TableRow, Typography } from '@mui/material'
import { Fragment } from 'react/jsx-runtime'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../components/custom'
import { AddRounded, RefreshRounded, RemoveRounded } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { selectSimulatorDate } from '../../selectors/simulator'
import { createStringDateFromStamp } from '../../util/tradePage'
import ExtraSmallTextField from '../../components/common/ExtraSmallTextField'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OptionOrderDataType, OrdersDataType } from '../../reducer/orders.reducer'
import { setSimulatorOrderDataFunction } from '../../reducer/simulator.reducer'
import { optionsTradesDataType, optionTradeDataType } from '../typings/options.typings'
import { findNearestNumber, getCurrentOrderLtpPriceFromData, orderTypeToTransactionType, roundToNearestMultiple} from '../../util/tradepanel'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { selectPredefinedStrategy, selectStrategyBuilderTab } from '../../selectors/strategyBuilder'
import { calculateSimulatorOrdersLiveData, setOptionChainDrawerOpen } from '../../reducer/options.reducer'
import { isFirstDateGreatedThanSecond } from '../../util/dateTime'
import commonActions from '../../actions/common.actions'
import { useDispatch } from 'react-redux'
import { ActionType, useUndoRedoContext } from '../../components/UndoRedoFunctonalities/UndoRedo.context'
import { getSecurityAsync } from '../../util/security'
import BinIcon from '../../assets/icons/BinIcon'
import ROUTES from '../../assets/routes'
import { matchPath } from 'react-router-dom'
import AddIcon from '../../assets/icons/AddIcon'
import { StrategyBuilderTab } from '../../components/strategyBuilder/header'

type Props = {}

const TradeContainer = (props: Props) => {
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const timeStamp = useSelector(selectSimulatorDate)
    const strategyName = useSelector(selectPredefinedStrategy)
    const { manager } = useUndoRedoContext()
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const strategySelectedTab = useSelector(selectStrategyBuilderTab)
    const { allOrders: orders, allOrdersPrices: ordersPrices } = useCombineBasketAnalyzeAndLegOrders()
    const {
        primaryOrUnderlyingToken: underlyingToken
    } = useSelectPrimaryOrUnderlyingSecurity()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const [mainSecurity, setMainSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (underlyingToken) getSecurityAsync(underlyingToken).then(setMainSecurity) }, [underlyingToken])
    const quantityInputRefs = useRef<Array<HTMLInputElement>>([])
    const setQuantityInputRef = (index: number) => (ref: HTMLInputElement) => {
        quantityInputRefs.current[index] = ref
    }
    const inputPricesRefs = useRef<Record<string, HTMLInputElement>>({})
    const setInputPricesRef = (index: string) => (ref: HTMLInputElement) => {
        inputPricesRefs.current[index] = ref
    }
    const [expiryAnchorEl, setExpiryAnchorEl] = useState<{
        index: number
        element: (HTMLElement | null)
    }>()
    const optionExpiriesMenu: {
        label: string
        value: number
    }[] = useMemo(() => {
        const filteredExpiries = (optionExpiries ?? []).filter(d => !d.derived)
        return filteredExpiries?.map(d => ({
            label: createStringDateFromStamp(Number(d.expiration.expiry?.expiry ?? 0)),
            value: d.expiration.expiry?.expiry ?? 0
        }))
    }, [optionExpiries])
    const optionExpiriesMenuValuesRef = useRef<number[]>([])
    const optionExpiriesMenuValues = useMemo(() => {
        optionExpiriesMenuValuesRef.current = optionExpiriesMenu.map(d => d.value)
        return optionExpiriesMenuValuesRef.current
    }, [optionExpiriesMenu])


    const renderOrdersCount = useMemo(() => {
        let result = 0
        orders.forEach((order, i) => {
            if (order.traded) return
            result += 1
        })
        return result
    }, [orders])

    const handleClearOrders = useCallback(() => {
        const newOrders = orders.filter(o => o.traded)
        const newOrdersPrices = ordersPrices.filter(o => o.traded)
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({
            orders: newOrders,
            orderPrices: newOrdersPrices,
            token: underlyingToken
        })
    }, [orders, ordersPrices, underlyingToken, push])

    useEffect(() => {
        if (orders.length) {
            const newOrders = orders.filter(o => o.traded || (!o.traded && !isFirstDateGreatedThanSecond(timeStamp, o.expiry ?? 0)))
            const newOrdersPrices = ordersPrices.filter((o, i) => o.traded || (!o.traded && !isFirstDateGreatedThanSecond(timeStamp, orders[i].expiry ?? 0)))
            let modified = 0
            newOrders.forEach((order, i) => {
                if (!order.traded) {
                    modified++
                    const newPrices = getCurrentOrderLtpPriceFromData(order, optionExpiries ?? [], [])
                    newOrdersPrices[i] = {
                        ...newPrices,
                        timeStamp: timeStamp
                    }
                    inputPricesRefs.current[order.uuid].value = newOrdersPrices[i].price
                    newOrders[i] = {
                        ...order,
                        timeStamp: timeStamp,
                    }
                }
            })
            if (orders.length !== newOrders.length) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    errorType: 'info',
                    message: 'Removed expired contracts from orders'
                })
            }
            if (modified > 0 || orders.length !== newOrders.length) {
                setSimulatorOrderDataFunction({
                    orders: newOrders,
                    orderPrices: newOrdersPrices,
                    token: underlyingToken
                })
            }
        }
    }, [timeStamp, optionExpiries])

    useEffect(() => {
        orders.forEach((order, i) => {
            if (!order.traded) {
                quantityInputRefs.current[i].value = (order.quantity * (order.security?.lotSize ?? 75)).toString()
                inputPricesRefs.current[order.uuid].value = ordersPrices[i].price
            }
        })
    }, [orders, ordersPrices])

    const handlePlaceOrder = useCallback(() => {
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        newOrders.forEach((order, i) => {
            if (order.timeStamp === timeStamp) {
                newOrders[i] = {
                    ...newOrders[i],
                    traded: true
                }
                newOrdersPrices[i] = {
                    ...newOrdersPrices[i],
                    traded: true
                }
            }
        })
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({
            orders: newOrders,
            orderPrices: newOrdersPrices,
            token: underlyingToken
        })
        calculateSimulatorOrdersLiveData(underlyingToken)
    }, [orders, ordersPrices, push, underlyingToken, timeStamp])

    const toggleVisibility = useCallback((orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        newOrders[i] = {
            ...newOrders[i],
            visibility: !newOrders[i].visibility
        }
        newOrdersPrices[i] = {
            ...newOrdersPrices[i],
            visibility: newOrders[i].visibility
        }
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
    }, [orders, ordersPrices, push])

    const toggleOrderType = useCallback((orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrders = [...orders]
        newOrders[i] = {
            ...newOrders[i],
            type: newOrders[i].type === 'buy' ? 'sell' : 'buy'
        }
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: ordersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders: newOrders })
    }, [orders, ordersPrices, push])

    const toggleCallPutType = useCallback(async (orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        if (orders[i]?.contractType !== 'OPTION') return
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        const priceMultiplier = newOrders[i].security?.priceMultiplier ?? 100
        newOrders[i] = {
            ...newOrders[i],
            callput: (newOrders[i] as OptionOrderDataType).callput === 'call' ? 'put' : 'call',
            security: (newOrders[i] as OptionOrderDataType).callput === 'call' ? (newOrders[i].data as optionTradeDataType).putOption?.security : (newOrders[i].data as optionTradeDataType).callOption?.security
        } as OrdersDataType
        const data = (newOrders[i] as OptionOrderDataType).callput === 'call' ? (newOrders[i].data as optionTradeDataType).callOption : (newOrders[i].data as optionTradeDataType).putOption
        newOrdersPrices[i] = {
            ...newOrdersPrices[i],
            price: ((data?.ltp ?? 0) / priceMultiplier).toFixed(2),
            mid: (data?.ltp ?? 0) / priceMultiplier
        }
        inputPricesRefs.current[orderId].value = newOrdersPrices[i].price
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({
            orders: newOrders,
            orderPrices: newOrdersPrices,
        })
    }, [orders, ordersPrices, push])

    const handleTextQuantityChange = useCallback((value: string, orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrders = [...orders]
        let roundedValue = (newOrders[i].security?.lotSize ?? 50) * Math.round(Number(value) / (newOrders[i].security?.lotSize ?? 50))
        if (roundedValue <= 0) roundedValue = (newOrders[i].security?.lotSize ?? 50)
        if (roundedValue >= (newOrders[i].security?.volumeFreezeQuantity ?? 0)) {
            roundedValue = (newOrders[i].security?.volumeFreezeQuantity ?? 0) - 1
        }
        newOrders[i] = {
            ...newOrders[i],
            quantity: Math.round(roundedValue / (newOrders[i].security?.lotSize ?? 50))
        }
        // if (newOrders?.length) setQuantityGcd(calculateGcd(newOrders.map(o => o.quantity)))
        if (JSON.stringify(orders) !== JSON.stringify(newOrders)) {
            push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: ordersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
            setSimulatorOrderDataFunction({
                orders: newOrders,
                token: '',
                resetPredefinedStrategy: false
            })
        }
    }, [orders, ordersPrices, push])

    const handleTextQuantityOnBlur = useCallback((value: string, orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrders = [...orders]
        let roundedValue = (newOrders[i].security?.lotSize ?? 50) * Math.round(Number(value) / (newOrders[i].security?.lotSize ?? 50))
        if (roundedValue <= 0) roundedValue = (newOrders[i].security?.lotSize ?? 50)
        if (roundedValue >= (newOrders[i].security?.volumeFreezeQuantity ?? 0)) {
            roundedValue = (newOrders[i].security?.volumeFreezeQuantity ?? 0) - 1
        }
        quantityInputRefs.current[i].value = roundedValue.toString()
    }, [orders])

    const handleQuantityChange = useCallback((orderId: string, type: 'inc' | 'dec') => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        if (type === 'inc') {
            let incValue = newOrders[i].quantity + 1
            if ((incValue * (newOrders[i].security?.lotSize ?? 50)) >= (newOrders[i].security?.volumeFreezeQuantity ?? 0)) {
                incValue = Math.round(((newOrders[i].security?.volumeFreezeQuantity ?? 0) - 1) / (newOrders[i].security?.lotSize ?? 50))
            }
            newOrders[i] = {
                ...newOrders[i],
                quantity: incValue
            }
            quantityInputRefs.current[i].value = ((newOrders[i].quantity) * (newOrders[i].security?.lotSize ?? 50)).toString()
        } else {
            if (newOrders[i].quantity > 1) {
                newOrders[i] = {
                    ...newOrders[i],
                    quantity: newOrders[i].quantity - 1
                }
                quantityInputRefs.current[i].value = ((newOrders[i].quantity) * (newOrders[i].security?.lotSize ?? 50)).toString()
            }
            // else {
            //     newOrders.splice(i, 1)
            //     newOrdersPrices.splice(i, 1)
            // }
        }
        // if (newOrders?.length) setQuantityGcd(calculateGcd(newOrders.map(o => o.quantity)))
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({
            orders: newOrders,
            orderPrices: newOrdersPrices
        })
    }, [orders, ordersPrices, push])

    const handleResetPrices = useCallback((ordersPassed = orders, ordersPricesPassed = ordersPrices) => {
        const newOrders = [...ordersPassed]
        const newOrderPrices = [...ordersPricesPassed]
        newOrders.forEach((order, i) => {
            if ((order.timeStamp ?? 0) !== timeStamp || order.traded) return
            if (order.contractType !== 'OPTION') return
            const option = (order as OptionOrderDataType).callput === 'call' ? order.data.callOption : order.data.putOption
            newOrderPrices[i] = {
                ...newOrderPrices[i],
                price: ((option?.ltp ?? 0) / (order.security?.priceMultiplier ?? 100)).toFixed(2),
                mid: (option?.ltp ?? 0) / (order.security?.priceMultiplier ?? 100)
            }
            if (inputPricesRefs.current[order.uuid]) {
                inputPricesRefs.current[order.uuid].value = newOrderPrices[i].price
            }
        })
        push({ prev: { orders: ordersPassed, ordersPrices: ordersPricesPassed }, new: { orders: ordersPassed, ordersPrices: ordersPricesPassed } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrderPrices, resetOrderState: false, resetPredefinedStrategy: false })
    }, [orders, ordersPrices, push, timeStamp])

    const handleExpiryChange = useCallback(async (orderId: string, value: number, orders: OrdersDataType[]) => {
        if (optionExpiries) {
            const i = orders.findIndex(o => o.uuid === orderId)
            const oldExpiry = orders[i].expiry
            const newOrders = [...orders]
            const newOrderPrices = [...ordersPrices]
            const r = optionExpiries.findIndex(e => e.expiration.expiry?.expiry === value)

            let newOptionsTradesData: optionsTradesDataType[] = JSON.parse(JSON.stringify(optionExpiries))
            let expirydata = newOptionsTradesData.find(o => o.expiration.expiry?.expiry === value)

            if (newOrders[i].contractType === 'OPTION') {
                if (r && mainSecurity && optionExpiries[r].trades && optionExpiries[r].trades.length <= 0) {
                    expirydata = newOptionsTradesData.find(o => o.expiration.expiry?.expiry === value)
                }
                const strikesMenu = expirydata?.trades.map(t => t.strikeCol.strike)

                const nearestStrike = findNearestNumber(strikesMenu ?? [], (newOrders[i] as OptionOrderDataType)?.strike)
                const data = expirydata?.trades?.find(t => t.strikeCol?.strike === nearestStrike)
                if (data) {
                    const option = (newOrders[i] as OptionOrderDataType).callput === 'call' ? data?.callOption : data?.putOption
                    newOrders[i] = {
                        ...newOrders[i],
                        data: data as optionTradeDataType,
                        expiry: expirydata?.expiration.expiry?.expiry ?? 0,
                        strike: data.strikeCol?.strike,
                        security: option?.security,
                        timeStamp: timeStamp
                    } as OrdersDataType
                }
            }
            newOrderPrices[i] = getCurrentOrderLtpPriceFromData(newOrders[i], optionExpiries ?? [], [])
            newOrderPrices[i].timeStamp = timeStamp
            push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: ordersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
            setSimulatorOrderDataFunction({
                orders: newOrders,
                orderPrices: newOrderPrices,
            })
            if (!optionExpiriesMenuValuesRef.current.includes(oldExpiry)) {
                handleResetPrices(newOrders, ordersPrices)
            }
        }
        setExpiryAnchorEl(undefined)
    }, [optionExpiries, ordersPrices, timeStamp, push, mainSecurity, handleResetPrices])

    const handleStrikeChange = useCallback(async (orderId: string, type: 'inc' | 'dec', orders: OrdersDataType[], global: boolean = false) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        if (orders[i].contractType !== 'OPTION') return orders
        if (!optionExpiries) return orders
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        let newOptionsTradesData = [...optionExpiries]
        const r = newOptionsTradesData.findIndex(o => o.expiration.expiry?.expiry === newOrders[i].security?.expiry?.expiry)
        const strikesMenu = newOptionsTradesData[r].trades.map(t => t.strikeCol.strike)
        const index = strikesMenu.findIndex(t => t === (newOrders[i] as OptionOrderDataType).data.strikeCol.strike)
        let incDecIndex
        if (type === 'inc') {
            if (index < strikesMenu.length - 1) {
                incDecIndex = index + 1
            }
        } else {
            if (index > 0) {
                incDecIndex = index - 1
            }
        }
        if (incDecIndex !== undefined && incDecIndex >= 0) {
            const newStrike = strikesMenu[incDecIndex]
            const data = optionExpiries.find(o => o.expiration.expiry?.expiry === newOrders[i].security?.expiry?.expiry)?.trades.find(t => t.strikeCol.strike === newStrike)
            if (data) {
                const option = (newOrders[i] as OptionOrderDataType).callput === 'call' ? data?.callOption : data?.putOption
                newOrders[i] = {
                    ...newOrders[i],
                    data: data,
                    strike: newStrike,
                    security: option?.security
                } as OrdersDataType
                const priceMultiplier = newOrders[i].security?.priceMultiplier ?? 100
                newOrdersPrices[i] = {
                    ...newOrdersPrices[i],
                    price: ((option?.ltp ?? 0) / priceMultiplier).toFixed(2),
                    mid: (option?.ltp ?? 0) / priceMultiplier
                }
                if (inputPricesRefs.current[orderId]) {
                    inputPricesRefs.current[orderId].value = newOrdersPrices[i].price
                }
            }
            push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: ordersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
            setSimulatorOrderDataFunction({
                orders: newOrders,
                orderPrices: newOrdersPrices
            })
        }
        return newOrders
    }, [optionExpiries, ordersPrices, push])

    const handlePriceBlur = (value: number, orderId: string) => {
        const i = orders.findIndex(o => o.uuid === orderId)
        const newOrdersPrices = [...ordersPrices]
        const price = roundToNearestMultiple(value, orderTypeToTransactionType(orders[i].type)).toFixed(2)
        newOrdersPrices[i] = {
            ...newOrdersPrices[i],
            price: price,
            topAsk: Number(price),
            topBid: Number(price)
        }
        if (inputPricesRefs.current[orderId]) {
            inputPricesRefs.current[orderId].value = price
        }
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: orders, ordersPrices: ordersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders, orderPrices: newOrdersPrices })
    }

    const strategyDisplayName = useMemo(() => {
        return (`${mainSecurity?.symbol ?? ''} - ${strategyName ?? 'Custom'}`)
    }, [mainSecurity?.symbol, strategyName])

    const handleClearTrade = useCallback((e: any) => {
        e.stopPropagation()
        manager.clear({})
        push({ previousTimeStamp: simulatorTimestamp, timeStamp: simulatorTimestamp }, 'simulator time change', ActionType.SIMULATOR_TIME_CHANGE)
        setSimulatorOrderDataFunction({ orders: [], resetOrderState: true })
    }, [dispatch, manager, push, simulatorTimestamp])

    const removeOrder = useCallback((orderId: string) => {
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        const index = newOrders.findIndex(o => o.uuid === orderId)
        if (index === -1) return
        newOrders.splice(index, 1)
        newOrdersPrices.splice(index, 1)
        setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
    }, [orders, ordersPrices, setSimulatorOrderDataFunction])

    const showAddLeg = useMemo(() => {
        const strategyPath = matchPath(ROUTES.simulator, window.location.pathname)
        const isOptionChainOpenInStrategy = strategySelectedTab === StrategyBuilderTab.OptionChain
        return !(strategyPath && isOptionChainOpenInStrategy)
    }, [window.location.pathname, strategySelectedTab])

    return renderOrdersCount === 0 ? null : (
        <Box sx={{ background: '#88888810', padding: '5px', width: '100%' }}>
            <Grid container item xs={12} justifyContent={'space-between'} sx={{ mb: 1 }}>
                <Grid container item
                    xs={8}>
                    <Typography fontSize={'14px'}
                        sx={{
                            width: '180px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                        style={{
                            fontWeight: '600',
                            padding: '5px 0 0 5px'
                        }}
                    >
                        {strategyDisplayName}
                    </Typography>
                </Grid>
                <Grid container justifyContent={'flex-end'} item
                    xs={4}>
                    {
                        showAddLeg ?
                            <NonInteractiveTooltip title="Add Leg" arrow placement="top">
                                <ExtraSmallButton
                                    data-event={'Add Leg Clicked'}
                                    data-event-source={'Trade'}
                                    variant="text"
                                    color={'inherit'}
                                    onClick={() => dispatch(setOptionChainDrawerOpen(true))}
                                    size={'small'}
                                    sx={{
                                        width: '30px',
                                        height: '30px'
                                    }}
                                >
                                    <AddIcon fontSize='medium' />
                                </ExtraSmallButton>
                            </NonInteractiveTooltip> : null
                    }
                    <NonInteractiveTooltip title="Reset Prices" arrow placement="top">
                        <ExtraSmallButton
                            data-event={'Reset Prices Clicked'}
                            data-event-source={'Trade'}
                            variant="text"
                            color={'inherit'}
                            className="reset-prices"
                            onClick={() => handleResetPrices()}
                            size={'small'}
                            sx={{
                                width: '30px',
                                height: '30px'
                            }}
                        >
                            <RefreshRounded fontSize='medium' className="reset-prices-icon" />
                        </ExtraSmallButton>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip title={"New Simulation"} arrow placement="top">
                        <ExtraSmallButton
                            data-event={'Clear Trade Clicked'}
                            data-event-source={'Trade'}
                            variant="text"
                            color={'inherit'}
                            onClick={handleClearTrade}
                            size={'small'}
                            sx={{
                                width: '30px',
                                height: '30px'
                            }}
                        >
                            <BinIcon fontSize='small' />
                        </ExtraSmallButton>
                    </NonInteractiveTooltip>
                </Grid>
            </Grid>
            <table style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                    <tr>
                        <th>
                            <Typography color={'text.secondary'} variant={'caption'}></Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant={'caption'}>B/S</Typography>
                        </th>
                        {/* <th>
                            <Typography color={'text.secondary'} variant="caption">Date</Typography>
                        </th> */}
                        <th>
                            <Typography color={'text.secondary'} variant="caption">EXPIRY</Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant="caption">STRIKE</Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant="caption">TYPE</Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant="caption">QTY</Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant="caption">PRICE</Typography>
                        </th>
                        <th>
                            <Typography color={'text.secondary'} variant={'caption'}></Typography>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {
                        orders.map((order, i) => {
                            if (order.traded) return null
                            return order.contractType === 'OPTION' ? <Fragment key={order.uuid}>
                                <TableRow key={order.uuid + i} sx={{
                                    height: '41px',
                                    fontSize: '0.875rem',
                                    '&.MuiTableRow-root td': {
                                        // background: hoveredOrderIndex === i ? theme => alpha(theme.palette.primary.main, 0.1) : 'none'
                                    },
                                    '&.MuiTableRow-root td:first-of-type': {
                                        borderTopLeftRadius: '2px',
                                        borderBottomLeftRadius: '2px'
                                    },
                                    '&.MuiTableRow-root td:last-of-type': {
                                        borderTopRightRadius: '2px',
                                        borderBottomRightRadius: '2px'
                                    }
                                }} style={{ height: '41px' }}
                                >
                                    <td>
                                        <Checkbox checked={order.visibility} onClick={() => toggleVisibility(order.uuid)} size="small" />
                                        {/* {
                                            <ExtraSmallButton data-event={'Visibility of Order Changed'}
                                                data-event-source={'Trade'} size={'small'}
                                                style={{
                                                    borderRadius: '2px',
                                                    height: '24px',
                                                    width: '24px'
                                                }}
                                                disabled={Boolean(order?.sqOffByOrderId)}
                                                onClick={() => toggleVisibility(order.uuid)}
                                            >
                                                {
                                                    order.visibility ?
                                                        <Show fontSize='small' color='action' /> :
                                                        <Hide fontSize='small' color='disabled' />
                                                }
                                            </ExtraSmallButton>
                                        } */}
                                    </td>
                                    <td align="center">
                                        <ExtraSmallButton data-event={'B/S Changed'}
                                            data-event-source={'Trade'} size={'small'}
                                            disabled={!order.visibility || Boolean(order?.sqOffByOrderId)}
                                            className={`${order.type === 'buy' ? 'buy-button' : 'sell-button'}`}
                                            onClick={() => toggleOrderType(order.uuid)}
                                        >
                                            {order.type === 'buy' ? 'B' : 'S'}
                                        </ExtraSmallButton>
                                    </td>
                                    <td align="center">
                                        <ExtraSmallButton style={{ height: '24px', padding: 0 }}
                                            variant="outlined"
                                            color={'inherit'}
                                            onClick={(e) => setExpiryAnchorEl({ index: i, element: e.currentTarget })}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    mx: 0.5,
                                                    fontSize: '0.875rem'
                                                }}
                                                className="order-buttons"
                                            >
                                                {createStringDateFromStamp(order.security?.expiry?.expiry ?? 0)}
                                            </Box>
                                        </ExtraSmallButton>
                                        <Popover
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'center'
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'center'
                                            }}
                                            anchorEl={expiryAnchorEl?.element}
                                            open={expiryAnchorEl?.index === i}
                                            onClose={() => setExpiryAnchorEl(undefined)}
                                        >
                                            {
                                                optionExpiriesMenu.map((ex) =>
                                                    <MenuItem data-event={'Expiry Changed'}
                                                        data-event-source={'Trade'}
                                                        key={ex.value}
                                                        selected={ex.value === order.security?.expiry?.expiry}
                                                        className="font-feature-num"
                                                        onClick={() => handleExpiryChange(order.uuid, ex.value, orders)}>{ex.label}</MenuItem>)
                                            }
                                        </Popover>
                                    </td>
                                    <td align="center">
                                        <ExtraSmallTextField
                                            textFieldProps={{
                                                id: 'strikes-textfield',
                                                hiddenLabel: true,
                                                className: 'rounded',
                                                value: (order.data.strikeCol.strike / (order.security?.priceMultiplier ?? 100)),
                                                type: 'number',
                                                inputProps: {
                                                    sx: {
                                                        maxWidth: 50
                                                    }
                                                },
                                                disabled: !order.visibility || Boolean(order?.sqOffByOrderId),
                                                InputProps: {
                                                    startAdornment: (<IconButton
                                                        onClick={() => handleStrikeChange(order.uuid, 'dec', orders)}
                                                        disabled={!order.visibility}
                                                        size={'small'}
                                                        sx={{ p: 0 }}><RemoveRounded
                                                            className="trade-panel-edit-icons" /></IconButton>),
                                                    endAdornment: (<IconButton
                                                        onClick={() => handleStrikeChange(order.uuid, 'inc', orders)}
                                                        disabled={!order.visibility}
                                                        size={'small'}
                                                        sx={{ p: 0 }}><AddRounded
                                                            className="trade-panel-edit-icons" /></IconButton>)
                                                }
                                            }}
                                        />
                                    </td>
                                    <td align="center">
                                        {order.contractType === 'OPTION' ?
                                            <ExtraSmallButton variant="outlined"
                                                className="order-buttons"
                                                style={{
                                                    height: '24px',
                                                    width: '32px',
                                                    padding: 0
                                                }}
                                                disabled={!order.visibility || Boolean(order?.sqOffByOrderId)}
                                                color={'inherit'}
                                                data-event={'C/P Changed'}
                                                data-event-source={'Trade'}
                                                onClick={() => toggleCallPutType(order.uuid)}
                                            ><span
                                                className="order-buttons">{(order as OptionOrderDataType).callput === 'call' ? 'CE' : 'PE'}</span></ExtraSmallButton> :
                                            <Typography sx={{ textAlign: 'center', width: '32px', fontSize: "12px" }}>FUT</Typography>}
                                    </td>
                                    <td align="center">
                                        <ExtraSmallTextField
                                            textFieldProps={{
                                                id: 'quantity-textfield',
                                                hiddenLabel: true,
                                                className: 'rounded',
                                                defaultValue: order.quantity * (order.security?.lotSize ?? 0),
                                                inputRef: setQuantityInputRef(i),
                                                onChange: (e) => handleTextQuantityChange(e.target.value, order.uuid),
                                                onBlur: (e) => handleTextQuantityOnBlur(e.target.value, order.uuid),
                                                type: 'number',
                                                disabled: !order.visibility || Boolean(order?.sqOffByOrderId),
                                                inputProps: {
                                                    sx: {
                                                        maxWidth: 32
                                                    },
                                                    step: 1,
                                                    min: 0,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*'
                                                },
                                                InputProps: {
                                                    startAdornment: (<IconButton
                                                        onClick={() => handleQuantityChange(order.uuid, 'dec')}
                                                        disabled={!order.visibility || Boolean(order?.sqOffByOrderId)}
                                                        size={'small'} sx={{ p: 0 }}><RemoveRounded
                                                            className="trade-panel-edit-icons" /></IconButton>),
                                                    endAdornment: (<IconButton
                                                        onClick={() => handleQuantityChange(order.uuid, 'inc')}
                                                        disabled={!order.visibility || Boolean(order?.sqOffByOrderId)}
                                                        size={'small'}
                                                        sx={{ p: 0 }}><AddRounded
                                                            className="trade-panel-edit-icons" /></IconButton>)
                                                }
                                            }}
                                        />
                                    </td>
                                    <td align="center" style={{ position: 'relative' }}>
                                        <span>
                                            <ExtraSmallTextField
                                                textFieldProps={{
                                                    defaultValue: ordersPrices[i]?.price,
                                                    inputRef: setInputPricesRef(order.uuid),
                                                    onBlur: (e) => handlePriceBlur(Number(e.target.value), order.uuid),
                                                    inputProps: {
                                                        sx: {
                                                            maxWidth: 54
                                                        }
                                                    },
                                                    disabled: !order.visibility || Boolean(order?.sqOffByOrderId),
                                                    type: 'number'
                                                }} hideEndIcon={true} />
                                        </span>
                                    </td>
                                    <td align="center">
                                        <IconButton sx={{ padding: 0 }} onClick={() => removeOrder(order.uuid)}><BinIcon fontSize='small' /></IconButton>
                                    </td>
                                </TableRow>
                                {
                                    (Boolean(optionExpiriesMenuValues.length) && !optionExpiriesMenuValues.includes(order.expiry)) &&
                                    <TableRow>
                                        <td colSpan={7}>
                                            <div className="rejected-order">
                                                Selected expiry is not available for this order. Please select a different expiry.
                                            </div>
                                        </td>
                                    </TableRow>
                                }
                            </Fragment> : null
                        })
                    }
                </tbody>
            </table>
            <Stack direction={'row'} gap={1} paddingRight={'6px'} justifyContent={'flex-end'}>
                <ExtraSmallButton variant='outlined' onClick={handleClearOrders}>Cancel</ExtraSmallButton>
                <ExtraSmallButton variant='contained' disabled={orders.filter(o => !o.traded).some(o => !optionExpiriesMenuValues.includes(o.expiry))} color='primary' onClick={handlePlaceOrder}>Place Order</ExtraSmallButton>
            </Stack>
        </Box>
    )
}

export default TradeContainer